'use client'

import React, { useState, createContext, useContext, ReactNode } from 'react'

import { programDetailScrollToElement } from '../../_utils'

/** Gender type for program selection */
type GenderType = 'male' | 'female'

/**
 * Context type for gender selection and navigation.
 */
interface IGenderContextType {
	/** Currently selected gender */
	gender: GenderType
	/** Sets the gender and optionally scrolls to a section */
	setGender: (gender: GenderType, scrollTo?: string) => void
	/** Scrolls to a section by ID */
	handleScrollTo: (scrollTo?: string) => void
}

const GenderContext = createContext<IGenderContextType | undefined>(undefined)

/**
 * Hook to access gender context for annual program details.
 * Must be used within a ZAPDetailGenderProvider.
 *
 * @throws {Error} If used outside of ZAPDetailGenderProvider
 * @returns Gender context with current gender and setter functions
 *
 * @example
 * ```typescript
 * const { gender, setGender, handleScrollTo } = useAPDetailGender()
 * ```
 */
export const useAPDetailGender = (): IGenderContextType => {
	const context = useContext(GenderContext)
	if (!context) {
		throw new Error('useAPDetailGender must be used within a ZAPDetailGenderProvider')
	}
	return context
}

/**
 * Props for the ZAPDetailGenderProvider component.
 */
interface IZAPDetailGenderProviderProps {
	/** Child components */
	children: ReactNode
	/** Initial gender selection (default: 'female') */
	initialGender?: GenderType
}

/**
 * Universal provider component for annual program detail gender selection.
 * Manages the selected gender state and provides scroll navigation functionality.
 * Shared across all annual program detail pages (Balance, Mini, Vital).
 *
 * @param children - Child components that will have access to the context
 * @param initialGender - Initial gender value (defaults to 'female')
 *
 * @example
 * ```typescript
 * <ZAPDetailGenderProvider initialGender='female'>
 *   <YourAnnualProgramContent />
 * </ZAPDetailGenderProvider>
 * ```
 */
export function ZAPDetailGenderProvider({
	children,
	initialGender = 'female'
}: IZAPDetailGenderProviderProps) {
	const [gender, setGender] = useState<GenderType>(initialGender)

	/**
	 * Handles scrolling to a section by ID.
	 * Uses the shared scroll utility with header offset.
	 *
	 * @param scrollTo - Optional section ID to scroll to
	 */
	const handleScrollTo = (scrollTo?: string): void => {
		if (!scrollTo) return
		programDetailScrollToElement(scrollTo)
	}

	return (
		<GenderContext.Provider value={{ gender, setGender, handleScrollTo }}>
			{children}
		</GenderContext.Provider>
	)
}
